import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { AiSummary } from "../components/AiSummary";
import { useMember } from "../lib/member";
import { ArrowLeft, Search, Printer, FileDown } from "lucide-react";
import { toast } from "sonner";
import { useFetch, useApi, lastMonth, inr } from "../lib/api";
import { Button } from "../components/ui/button";
import { PageHeader, Pill, Empty } from "../components/shared";
import { FilingsTable } from "./MyFilings";
import { MonthPicker } from "../components/DateField";

const GRADE = { A: "green", B: "blue", C: "amber", D: "red" };

const Kpi = ({ label, value, tone = "text-white", testid }) => (
  <div className="c-surface p-4 c-rise" data-testid={testid}><div className="text-xs text-slate-400">{label}</div>
    <div className={`font-display text-2xl font-semibold mt-1 ${tone}`}>{value}</div></div>);

const Section = ({ title, children, testid }) => (
  <section className="mt-8" data-testid={testid}><div className="c-eyebrow mb-3">{title}</div>{children}</section>);

const Info = ({ v, emp }) => (
  <div className="c-surface p-5 c-rise grid gap-x-8 gap-y-2 sm:grid-cols-2 lg:grid-cols-4 text-sm" data-testid="cp-details">
    {[["Principal employer", emp?.company_name], ["PAN", v.pan_number], ["EPF code", v.epf_registration_number], ["ESIC code", v.esic_registration_number],
      ["CLRA licence", v.clra_license_number], ["CLRA valid till", v.clra_license_expiry], ["CLRA max workers", v.clra_max_workers], ["Contact email", v.contact_email]].map(([l, x]) => (
      <div key={l}><div className="text-[11px] text-slate-500">{l}</div><div className="font-mono text-xs break-all">{x || "—"}</div></div>))}
  </div>);

const ScoreBox = ({ s }) => !s || s.score == null ? <Empty text="No wages this month, so there is no score." /> : (
  <div className="c-surface p-5 c-rise grid md:grid-cols-[160px_1fr] gap-5 items-start min-w-0" data-testid="cp-score">
    <div><span className="font-display text-5xl font-semibold" data-testid="cp-score-value">{s.score}</span>
      <div className="mt-2 flex flex-wrap gap-1.5"><Pill tone={GRADE[s.grade]}>Grade {s.grade}</Pill>{s.in_progress && <Pill tone="slate" testid="cp-score-in-progress">In progress</Pill>}</div>
      <div className="text-xs text-slate-500 mt-2">6-month avg {s.average_6m ?? "—"}</div></div>
    <div className="grid gap-1.5 text-xs">{s.breakdown.map((b) => (
      <div key={b.key} className="grid grid-cols-[1fr_50px] md:grid-cols-[170px_1fr_50px] gap-x-3 min-w-0"><span className="text-slate-300">{b.label}</span>
        <span className="text-slate-500 col-span-2 md:col-span-1 order-3 md:order-none break-words">{b.note}</span><span className={`font-mono text-right ${b.points < b.max ? "text-rose-300" : "text-emerald-300"}`}>{b.points}/{b.max}</span></div>))}</div>
  </div>);

const Holds = ({ holds, alerts }) => (!holds.length && !alerts.length ? <Empty text="No payment holds or open alerts." /> : (
  <div className="grid gap-2" data-testid="cp-holds">
    {holds.map((h) => (
      <div key={h.id} className={`rounded-lg border px-4 py-2.5 text-sm ${h.released ? "border-[#1F2A3E] text-slate-400" : "border-rose-500/40 bg-rose-500/5 text-rose-100"}`} data-testid="cp-hold-row">
        <span className="font-mono text-xs mr-2">{h.billing_month}</span><b>{h.released ? "Released" : "On hold"}</b> · {h.reason_code} — {h.reason}</div>))}
    {alerts.map((a) => (
      <div key={a.id} className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-2.5 text-sm text-amber-100" data-testid="cp-alert-row">
        <span className="font-mono text-xs mr-2">{a.month || ""}</span>{a.title}</div>))}
  </div>));

const Workers = ({ list }) => {
  const [q, setQ] = useState("");
  const rows = list.filter((w) => !q || `${w.name} ${w.uan || ""} ${w.department || ""} ${w.site || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (<>
    <div className="relative mb-3 max-w-xs"><Search size={14} className="absolute left-3 top-3 text-slate-500" />
      <input className="c-input !pl-9" placeholder="Search name, UAN, site…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="cp-worker-search" /></div>
    {!rows.length ? <Empty text="No workers match." /> : (
      <div className="c-surface overflow-x-auto c-rise"><table className="c-table w-full">
        <thead><tr><th>Worker</th><th>UAN</th><th>ESIC IP</th><th>Skill</th><th>Site</th><th>Department</th><th>Status</th><th>Paid this month</th></tr></thead>
        <tbody>{rows.map((w) => (
          <tr key={w.id} data-testid="cp-worker-row">
            <td><Link to={`/workers/${w.id}`} className="hover:text-[#00E599]">{w.name}</Link></td>
            <td className="font-mono text-xs">{w.uan || <span className="text-amber-300">missing</span>}</td>
            <td className="font-mono text-xs">{w.esic || <span className="text-amber-300">missing</span>}</td>
            <td className="text-xs">{w.skill}</td><td className="text-xs">{w.site || "—"}</td><td className="text-xs">{w.department || "—"}</td>
            <td><Pill tone={w.active ? "green" : "slate"}>{w.active ? "Active" : "Inactive"}</Pill></td>
            <td className="text-xs">{w.paid ? "Yes" : "—"}</td></tr>))}</tbody></table></div>)}
  </>);
};

const Reminders = ({ list }) => (!list.length ? <Empty text="No challan reminders sent to this contractor yet." /> : (
  <div className="c-surface overflow-x-auto c-rise" data-testid="cp-reminders">
    <table className="c-table w-full">
      <thead><tr><th>Sent</th><th>Month</th><th>Reminder</th><th>Missing</th><th>Emailed to</th><th>Copies</th></tr></thead>
      <tbody>{list.map((r) => (
        <tr key={r.id} data-testid="cp-reminder-row">
          <td className="font-mono text-xs whitespace-nowrap">{new Date(r.at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</td>
          <td className="font-mono text-xs">{r.month}</td>
          <td><Pill tone={r.stage === "final" ? "red" : "amber"}>{r.stage === "final" ? "Final" : "First"}</Pill></td>
          <td className="text-xs">{r.missing.join(", ")}</td>
          <td className="text-xs">{(r.to || []).join(", ") || <span className="text-amber-300">no contact email</span>}</td>
          <td className="text-xs text-slate-400" title={(r.copies || []).join(", ")}>{(r.copies || []).length ? `${r.copies.length} cop${r.copies.length > 1 ? "ies" : "y"}` : "—"}</td>
        </tr>))}</tbody>
    </table>
  </div>));

const outline = "border-[#1F2A3E] hover:bg-[#1A2234] hover:text-white";
const PdfActions = ({ vid, month, name }) => {
  const api = useApi();
  const [withWorkers, setWithWorkers] = useState(true);
  const download = async () => {
    try {
      const r = await api.get(`/pe/contractor/${vid}/profile.pdf`, { params: { month, workers: withWorkers }, responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data);
      a.download = `Contractor_Profile_${(name || "").replace(/[^A-Za-z0-9]+/g, "_")}_${month}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch { toast.error("PDF download failed"); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer"><input type="checkbox" className="accent-[#00E599]" checked={withWorkers} onChange={(e) => setWithWorkers(e.target.checked)} data-testid="cp-include-workers" />Include full worker list</label>
      <Button variant="outline" className={outline} onClick={() => window.open(`/print-contractor/${vid}?month=${month}&workers=${withWorkers ? 1 : 0}`, "_blank")} data-testid="cp-print-btn"><Printer size={14} className="mr-1.5" />Print</Button>
      <Button variant="outline" className={outline} onClick={download} data-testid="cp-pdf-btn"><FileDown size={14} className="mr-1.5" />Download PDF</Button>
    </div>);
};

export default function ContractorProfile() {
  const { vid } = useParams();
  const [month, setMonth] = useState(lastMonth());
  const { data, error } = useFetch(`/pe/contractor/${vid}/profile?month=${month}`);
  const { me } = useMember();
  if (me?.role === "vendor") return <Navigate to="/" replace />;
  if (error) return <Empty text="Contractor not found or not visible to you." />;
  return (
    <div data-testid="contractor-profile-page">
      <Link to="/vendors" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white mb-3" data-testid="cp-back"><ArrowLeft size={13} />Contractors</Link>
      <PageHeader eyebrow="Contractor profile" title={data?.vendor.vendor_name || "…"} desc="Workers, statutory filings, compliance score and payment holds for this contractor in one place.">
        <div className="flex flex-wrap items-center gap-3">
          <MonthPicker value={month} onChange={setMonth} testid="cp-month" className="w-44" />
          <PdfActions vid={vid} month={month} name={data?.vendor.vendor_name} /></div>
      </PageHeader>
      {!data ? <p className="text-xs text-slate-500 font-mono">Loading…</p> : <>
        <Info v={data.vendor} emp={data.employer} />
        <AiSummary vid={vid} month={month} />
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mt-4">
          <Kpi label="Active workers" value={data.workers.active} testid="cp-kpi-active" />
          <Kpi label={`Paid in ${month}`} value={data.workers.paid_this_month} testid="cp-kpi-paid" />
          <Kpi label="Gross wages" value={inr(data.wages.gross)} testid="cp-kpi-gross" />
          <Kpi label="PF + ESIC due" value={inr(data.wages.pf + data.wages.esic)} testid="cp-kpi-dues" />
          <Kpi label="Overdue filings" value={data.filings.overdue} tone={data.filings.overdue ? "text-rose-300" : "text-emerald-300"} testid="cp-kpi-overdue" />
          <Kpi label="Score" value={data.score?.score ?? "—"} tone={data.score?.grade === "D" ? "text-rose-300" : "text-white"} testid="cp-kpi-score" />
        </div>
        {(data.workers.no_uan > 0 || data.workers.no_esic > 0 || data.wages.breaches > 0) && (
          <p className="mt-3 text-xs text-amber-200" data-testid="cp-gaps">
            {[data.workers.no_uan && `${data.workers.no_uan} active worker(s) without UAN`, data.workers.no_esic && `${data.workers.no_esic} without ESIC IP`,
              data.wages.breaches && `${data.wages.breaches} wage row(s) below minimum wage in ${month}`].filter(Boolean).join(" · ")}</p>)}
        <Section title="Payment holds & open alerts" testid="cp-holds-section"><Holds holds={data.holds} alerts={data.alerts} /></Section>
        <Section title={`Compliance score · ${month}`} testid="cp-score-section"><ScoreBox s={data.score} /></Section>
        <Section title="Statutory filings (PF & ESIC, last 12 months)" testid="cp-filings-section">
          {data.filings.rows.length ? <FilingsTable rows={data.filings.rows} /> : <Empty text="No PF or ESIC dues yet." />}</Section>
        <Section title="Challan reminders sent" testid="cp-reminders-section"><Reminders list={data.reminders} /></Section>
        <Section title={`Workers (${data.workers.total})`} testid="cp-workers-section"><Workers list={data.workers.list} /></Section>
      </>}
    </div>);
}
